import { Injectable } from '@angular/core';
import dayjs, { Dayjs } from 'dayjs/esm/index.js';
import { EventNeoTyped } from '../interfaces/ficha-event.interface';
import { FichaSettings, FichaStates } from '../interfaces/ficha-event.interface';

@Injectable({
  providedIn: 'root',
})
export class FichaStatesBuilderRulesService {
  isEventInProgress(event: EventNeoTyped, settings: FichaSettings): boolean {
    if (!this.isToday(settings)) {
      return false;
    }

    const hasStartedAndNotEnded = !!event.event_start_clock_in && !event.event_end_clock_out;
    const isTemporaryEvent = event.id === 0 && event.eventTypeData.eventTypeId !== 'jornadaFlexible';
    return hasStartedAndNotEnded || isTemporaryEvent;
  }

  isEventPending(event: EventNeoTyped): boolean {
    return !event.event_start_clock_in && !event.event_end_clock_out;
  }

  canStartExpandedEvent(event: EventNeoTyped, settings: FichaSettings): boolean {
    return this.isToday(settings) && event.event_start_clock_in === null && event.event_end_clock_out === null;
  }

  canShowWorkEnd(event: EventNeoTyped, settings: FichaSettings): boolean {
    return this.isToday(settings) && event.event_start_clock_in !== null && event.event_end_clock_out === null;
  }

  canShowNewEventIn(event: EventNeoTyped, settings: FichaSettings): boolean {
    return this.isToday(settings) && event.id === 0 && (event.type_of_flexible === 'total' || event.type_of_flexible === 'jornada');
  }

  canShowHoraExtraIn(event: EventNeoTyped, settings: FichaSettings): boolean {
    return this.isToday(settings) && event.id === 0 && event.working_hours_expected_overtime === 1 && event.working_hours_expected === 0;
  }

  hasInProgressStartInFutureOrNow(eventInProgress?: EventNeoTyped, now: Dayjs = dayjs()): boolean {
    if (!eventInProgress?.event_start_clock_in) {
      return false;
    }

    return !dayjs(eventInProgress.event_start_clock_in).isBefore(now);
  }

  canShowOvertimeBlock(eventos: EventNeoTyped[], settings: FichaSettings, states: FichaStates, now: Dayjs = dayjs()): boolean {
    const permisoAbierto = !!states.meta?.permisosDelDiaActivos;
    const pausaAbierta = !!states.meta?.pausasActivas;

    const atLeastOneAllowed = eventos.some((event) => {
      const isHoraExtraOpen = this.isOvertimeEvent(event) && event.event_start_clock_in != null && event.event_end_clock_out == null;
      const isAllowedType = event.type === 'work' || event.type === 'others';
      if (!isAllowedType || !this.isToday(settings)) {
        return false;
      }

      const hasNoClocking = event.event_start_clock_in == null && event.event_end_clock_out == null;
      const hasBothClockings = event.event_start_clock_in != null && event.event_end_clock_out != null;

      let isWindowAllowed = hasNoClocking;
      if (hasBothClockings) {
        const startClockIn = event.event_start_clock_in;
        const endClockOut = event.event_end_clock_out;
        if (startClockIn != null && endClockOut != null) {
          const nowInside = this.isInsideEventWindow(now, startClockIn, endClockOut);
          isWindowAllowed = !nowInside || (nowInside && event.type === 'others');
        }
      }

      const canCreateBecauseNoOpenSubEvents = event.event_start_clock_in === null && !permisoAbierto && !pausaAbierta;
      const eventAlreadyClosed = event.event_end_clock_out != null;
      const endAt = hasBothClockings ? dayjs(event.event_end_clock_out) : undefined;
      const endExpectedPassed = !!endAt && (now.isAfter(endAt, 'minute') || now.isSame(endAt, 'minute'));
      const noOpenEventBlocking = canCreateBecauseNoOpenSubEvents || eventAlreadyClosed || endExpectedPassed || isHoraExtraOpen;

      return isWindowAllowed && noOpenEventBlocking;
    });

    return atLeastOneAllowed || eventos.length === 0;
  }

  private isToday(settings: FichaSettings): boolean {
    return settings.whenIsIt === 'today';
  }

  private isOvertimeEvent(event: EventNeoTyped): boolean {
    return event.working_hours_expected === 0 && event.working_hours_expected_overtime !== 0 && !event.is_flexible;
  }

  private isInsideEventWindow(now: Dayjs, start: string, end: string): boolean {
    const startAt = dayjs(start);
    const endAt = dayjs(end);
    return now.isAfter(startAt, 'minute') && now.isBefore(endAt, 'minute');
  }
}
